import Layout from "@/components/Layout";
import { CatalogEntityCard } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import PermissionDenied from "@/components/PermissionDenied";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import {
  createCurrency,
  currencyApiErrorMessage,
  deleteCurrency,
  listCurrencies,
  updateCurrency,
  type Currency,
  type CurrencyDraft,
  type SymbolPosition,
} from "@/lib/currencies/currenciesStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import {
  Coins,
  Edit,
  Filter,
  LayoutGrid,
  List,
  Loader2,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

const SYMBOL_POSITION_OPTIONS: { value: SymbolPosition; label: string; example: string }[] = [
  { value: "left_with_space", label: "Left with Space", example: "$ 100.00" },
  { value: "left", label: "Left", example: "$100.00" },
  { value: "right_with_space", label: "Right with Space", example: "100.00 $" },
  { value: "right", label: "Right", example: "100.00$" },
];

const emptyDraft = (): CurrencyDraft => ({
  code: "",
  name: "",
  symbol: "",
  symbolPosition: "left_with_space",
  decimalPlaces: 2,
  decimalSeparator: ".",
  thousandSeparator: ",",
  isActive: true,
});

type CurrencySortBy =
  | "code_asc"
  | "code_desc"
  | "name_asc"
  | "name_desc"
  | "latest"
  | "oldest";

export default function Currencies() {
  const authUser = getStoredAuthUser();
  const canViewAny = can(authUser, "currency_can_view_any");
  const canCreate = can(authUser, "currency_can_create");
  const canUpdate = can(authUser, "currency_can_update");
  const canDelete = can(authUser, "currency_can_delete");

  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterCode, setFilterCode] = useState("");
  const [filterSymbol, setFilterSymbol] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<CurrencySortBy>("code_asc");
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");

  const [perPage, setPerPage] = useState<number>(20);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Currency | null>(null);
  const [draft, setDraft] = useState<CurrencyDraft>(emptyDraft());
  const [saving, setSaving] = useState(false);

  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const fetchItems = useCallback(async (pageToLoad: number, pageSize: number) => {
    setLoading(true);
    const effectiveSearch = (filterName || filterCode || filterSymbol || searchQuery).trim();
    try {
      const res = await listCurrencies({
        page: pageToLoad,
        per_page: pageSize,
        search: effectiveSearch || undefined,
        sort: sortBy,
        is_active: activeFilter === "all" ? null : activeFilter === "active",
      });
      setCurrencies(res.data);
      setTotalCount(res.meta.total);
    } catch (err) {
      toast.error(currencyApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterName, filterCode, filterSymbol, activeFilter, sortBy]);

  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(totalCount / perPage));
  const startIndex = totalCount === 0 ? 0 : (page - 1) * perPage + 1;
  const endIndex = totalCount === 0 ? 0 : Math.min(page * perPage, totalCount);

  useEffect(() => {
    if (canViewAny) {
      void fetchItems(page, perPage);
    }
  }, [canViewAny, fetchItems, page, perPage]);

  const openCreate = () => {
    setEditingItem(null);
    setDraft(emptyDraft());
    setModalOpen(true);
  };

  const openEdit = (item: Currency) => {
    setEditingItem(item);
    setDraft({
      code: item.code,
      name: item.name,
      symbol: item.symbol,
      symbolPosition: item.symbolPosition,
      decimalPlaces: item.decimalPlaces,
      decimalSeparator: item.decimalSeparator,
      thousandSeparator: item.thousandSeparator,
      isActive: item.isActive,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setDraft(emptyDraft());
  };

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!draft.code.trim() || !draft.name.trim() || !draft.symbol.trim()) {
      toast.error("Please fill in Code, Name, and Symbol.");
      return;
    }

    setSaving(true);
    try {
      if (editingItem) {
        await updateCurrency(editingItem.id, draft);
        toast.success("Currency updated successfully.");
      } else {
        await createCurrency(draft);
        toast.success("Currency created successfully.");
      }
      closeModal();
      void fetchItems(page, perPage);
    } catch (err) {
      toast.error(currencyApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      await deleteCurrency(pendingDeleteId);
      toast.success("Currency deleted successfully.");
      setPendingDeleteId(null);
      void fetchItems(page, perPage);
    } catch (err) {
      toast.error(currencyApiErrorMessage(err));
    }
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filterName.trim()) count++;
    if (filterCode.trim()) count++;
    if (filterSymbol.trim()) count++;
    if (activeFilter !== "all") count++;
    return count;
  }, [filterName, filterCode, filterSymbol, activeFilter]);

  const clearFilters = () => {
    setSearchQuery("");
    setFilterName("");
    setFilterCode("");
    setFilterSymbol("");
    setActiveFilter("all");
    setSortBy("code_asc");
    setPage(1);
  };

  if (!canViewAny) {
    return (
      <Layout>
        <PermissionDenied message="You do not have permission to view currencies." />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header matching Banks.tsx */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Currencies</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage system currencies, symbols, positions, and formatting rules.{" "}
              <Link to="/dashboard" className="text-primary hover:underline font-medium">
                Home
              </Link>
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate}>
              <Plus className="w-4 h-4" />
              Add currency
            </Button>
          ) : null}
        </div>

        {/* Toolbar matching Banks.tsx */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by code, name, symbol…"
                className="glass-input pl-9 h-10"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {activeFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {activeFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter currencies</SheetTitle>
                  <SheetDescription>Filter by name, code, symbol, and status.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Currency filter</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Name contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterName}
                        onChange={(e) => {
                          setFilterName(e.target.value);
                          setPage(1);
                        }}
                        placeholder="e.g. Dollar, Rupee"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Code contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterCode}
                        onChange={(e) => {
                          setFilterCode(e.target.value);
                          setPage(1);
                        }}
                        placeholder="e.g. USD, PKR"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Symbol contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterSymbol}
                        onChange={(e) => {
                          setFilterSymbol(e.target.value);
                          setPage(1);
                        }}
                        placeholder="e.g. $, Rs."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Status</Label>
                      <Select
                        value={activeFilter}
                        onValueChange={(v: "all" | "active" | "inactive") => {
                          setActiveFilter(v);
                          setPage(1);
                        }}
                      >
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All status</SelectItem>
                          <SelectItem value="active">Active only</SelectItem>
                          <SelectItem value="inactive">Inactive only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={clearFilters}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          {/* Desktop Toolbar Row */}
          <div className="hidden md:flex items-center justify-between gap-3 pt-1">
            <GridPerPageSelect
              id="currency-page-size-desktop"
              pageSize={perPage}
              onPageSizeChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
              options={PAGE_SIZE_OPTIONS}
            />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[200px] shrink-0">
                <Select value={sortBy} onValueChange={(v: CurrencySortBy) => setSortBy(v)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="code_asc">Code: A to Z</SelectItem>
                    <SelectItem value="code_desc">Code: Z to A</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content View */}
        {loading ? (
          <div className="flex h-64 items-center justify-center rounded-xl border border-border/50 bg-card/20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : currencies.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center rounded-xl border border-border/50 bg-card/20 p-6 text-center">
            <Coins className="h-10 w-10 text-muted-foreground/50 mb-3" />
            <h3 className="font-semibold text-foreground">No currencies found</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm">
              {activeFiltersCount > 0
                ? "No currencies match your filters. Try clearing your search query."
                : "No currencies created yet. Click 'Add currency' to add one."}
            </p>
            {activeFiltersCount > 0 ? (
              <Button variant="outline" size="sm" onClick={clearFilters} className="mt-4">
                Clear filters
              </Button>
            ) : null}
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {currencies.map((c) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <CatalogEntityCard
                  title={`${c.name} (${c.code})`}
                  titleIcon={Coins}
                  statusBadge={{
                    label: c.isActive ? "Active" : "Inactive",
                    className: c.isActive
                      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      : "bg-muted text-muted-foreground",
                  }}
                  primaryMeta={{
                    label: "Symbol / Position",
                    value: `${c.symbol} (${c.symbolPosition})`,
                    icon: Coins,
                  }}
                  secondaryMeta={{
                    label: "Formatting",
                    value: `${c.decimalPlaces} decimals · "${c.decimalSeparator}"/"${c.thousandSeparator || "none"}"`,
                    icon: Coins,
                  }}
                  onEdit={canUpdate ? () => openEdit(c) : undefined}
                  onDelete={canDelete ? () => setPendingDeleteId(c.id) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {currencies.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    <Coins className="w-8 h-8 text-amber-500/60" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-foreground truncate">
                      {c.name} <span className="font-mono text-xs font-normal text-muted-foreground">({c.code})</span>
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                      Symbol: <span className="font-medium text-foreground">{c.symbol}</span> · Position: <span className="font-mono">{c.symbolPosition}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium mr-2",
                          c.isActive ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-muted text-muted-foreground"
                        )}
                      >
                        {c.isActive ? "Active" : "Inactive"}
                      </span>
                      {c.decimalPlaces} Decimals ({c.decimalSeparator}/{c.thousandSeparator || "none"})
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => openEdit(c)}>
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() => setPendingDeleteId(c.id)}
                        aria-label="Delete currency"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Footer Pagination matching Banks.tsx */}
        {!loading && currencies.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={startIndex}
            rangeEnd={endIndex}
            page={page}
            totalPages={pageCount}
            onPageChange={(p) => setPage(p)}
          />
        ) : null}

        {/* Create / Edit Dialog Modal matching Banks.tsx */}
        <Dialog
          open={modalOpen}
          onOpenChange={(o) => {
            if (!o) closeModal();
            else setModalOpen(true);
          }}
        >
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? "Edit currency" : "Add currency"}</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSave} className="space-y-4 py-1">
              <div className="space-y-1.5">
                <Label htmlFor="currency-code">Currency Code *</Label>
                <Input
                  id="currency-code"
                  placeholder="e.g. USD, PKR, EUR"
                  value={draft.code}
                  onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))}
                  maxLength={10}
                  className="glass-input uppercase"
                  required
                  autoFocus
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="currency-name">Currency Name *</Label>
                <Input
                  id="currency-name"
                  placeholder="e.g. US Dollar, Pakistani Rupee"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  className="glass-input"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="currency-symbol">Symbol *</Label>
                <Input
                  id="currency-symbol"
                  placeholder="e.g. $, Rs., €"
                  value={draft.symbol}
                  onChange={(e) => setDraft((d) => ({ ...d, symbol: e.target.value }))}
                  className="glass-input"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label>Symbol Position</Label>
                <Select
                  value={draft.symbolPosition}
                  onValueChange={(v: SymbolPosition) => setDraft((d) => ({ ...d, symbolPosition: v }))}
                >
                  <SelectTrigger className="glass-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SYMBOL_POSITION_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label} ({opt.example})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1.5">
                  <Label htmlFor="decimal-places">Decimals</Label>
                  <Input
                    id="decimal-places"
                    type="number"
                    min={0}
                    max={6}
                    value={draft.decimalPlaces}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, decimalPlaces: Math.max(0, minVal(Number(e.target.value), 6)) }))
                    }
                    className="glass-input"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="decimal-sep">Decimal Sep</Label>
                  <Input
                    id="decimal-sep"
                    value={draft.decimalSeparator}
                    onChange={(e) => setDraft((d) => ({ ...d, decimalSeparator: e.target.value }))}
                    className="glass-input text-center"
                    maxLength={5}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="thousand-sep">Thousand Sep</Label>
                  <Input
                    id="thousand-sep"
                    value={draft.thousandSeparator}
                    onChange={(e) => setDraft((d) => ({ ...d, thousandSeparator: e.target.value }))}
                    className="glass-input text-center"
                    maxLength={5}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 rounded-xl border border-border/50 bg-muted/10 px-3 py-2.5">
                <Checkbox
                  id="currency-active"
                  checked={draft.isActive}
                  onCheckedChange={(v) => setDraft((d) => ({ ...d, isActive: Boolean(v) }))}
                />
                <Label htmlFor="currency-active" className="cursor-pointer text-sm font-normal">
                  Active — show in transaction pickers
                </Label>
              </div>

              <DialogFooter className="gap-2 sm:gap-2 pt-2">
                <Button type="button" variant="glass" onClick={closeModal} disabled={saving}>
                  Cancel
                </Button>
                <Button type="submit" variant="premium" disabled={saving}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {editingItem ? "Update currency" : "Save currency"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Confirm Delete Dialog */}
        <ConfirmAlertDialog
          open={pendingDeleteId !== null}
          onOpenChange={(open) => {
            if (!open) setPendingDeleteId(null);
          }}
          title="Delete Currency?"
          description="Are you sure you want to delete this currency? This action cannot be undone."
          confirmLabel="Delete"
          destructive
          onConfirm={handleDelete}
        />
      </div>
    </Layout>
  );
}

function minVal(n: number, max: number): number {
  if (isNaN(n)) return 0;
  return Math.min(n, max);
}
